import React, { useEffect, useRef } from "react";

import {
  getGroupShellKey,
  getGroupShellTitle,
} from "@/components/business/FillInTableContainer/shared/utils/getTextFromData";
import { RegulatorySection } from "@/types/regulatory.types";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import {
  GroupShellDividerPlusBar,
  TableCreatorGroupShellTitle,
} from "@FillInTableContainer/shared";
import { useGetNumberOfShell } from "@FillInTableContainer/shared/hooks/useGetNumberOfShell";
import { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import {
  isSeparatedShellTitle,
  isAddDeleteGroupShell,
} from "@FillInTableContainer/shared/utils/getOptionsFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import RegulatoryTableCreatorShell from "./components/RegulatoryTableCreatorShell";
import { useHandleRegulatoryGroupShell } from "./hooks/useHandleRegulatoryGroupShell";
import * as S from "./RegulatoryTableCreatorGroupShell.style";

interface Props {
  groupTableKey: RegulatorySection;
  groupTableIndex: number;
  groupShellKey: string | null;
  shellList: TableCreatorShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  groupShellCount: number;
  isTableScroll: boolean;
}

const isNotFocusGroupShell = ["Additional"];

const RegulatoryTableCreatorGroupShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  shellList,
  tableKey,
  tableIndex,
  groupShellIndex,
  groupShellCount,
  isTableScroll,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  };
  const { shellCount } = useGetNumberOfShell();
  const articleSection = useArticleSection();
  const selectButtonRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    isLastGroupShell,
    isFocusedAddButton,
    handleDefaultFocus,
    clickAddGroupShellButton,
    handleClickToDeleteGroupShell,
    handleFocusGroupShell,
  } = useHandleRegulatoryGroupShell({ positionPropsInfo, groupShellCount });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);
        const clickGroupShellTitle = titleRef.current?.contains(target);

        if (clickSelectButton || clickGroupShellTitle) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isDeleteGroupShell, isFocused, isFocusedAddButton]);

  const isPossibleSelected = groupShellKey
    ? !isNotFocusGroupShell.includes(groupShellKey)
    : true;

  return (
    <>
      {isAddDeleteGroupShell(articleSection, groupShellKey) && (
        <GroupShellDividerPlusBar
          onClick={(event) =>
            clickAddGroupShellButton(event, {
              tableIndexParameter: tableIndex,
              groupShellIndexParameter: groupShellIndex,
              groupShellKeyParameter: groupShellKey,
            })
          }
          isLastPosition={false}
          isVisibility={isFocusedAddButton}
        />
      )}
      <S.GroupShellOutsideWrapper
        isSeparatedTitleRow={isSeparatedShellTitle(
          articleSection,
          groupShellKey,
        )}
        isFocused={isFocused}
        isClearGroupShell={isClearGroupShell}
        isDeleteGroupShell={isDeleteGroupShell}
        isAlertGroupShell={isAlertGroupShell}
      >
        {(isClearGroupShell || isDeleteGroupShell) && isEnableEdit && (
          <S.FixedDeleteButtonWrapper
            groupShellIndex={groupShellIndex}
            isShellTitle={getGroupShellKey(articleSection, groupShellKey)}
          >
            <S.DeleteTableButtonWrapper>
              <S.DeleteTableButton
                data-button="delete-button"
                onClick={handleClickToDeleteGroupShell}
              >
                <img src={IconXMark} alt="button for clear Row" />
                {focusStatus.type === "clear"
                  ? `Clear ${
                      ["Approval Process Contents"].includes(groupTableKey)
                        ? "Row"
                        : ""
                    }`
                  : null}
                {focusStatus.type === "delete"
                  ? `Delete ${
                      ["Approval Process Contents"].includes(groupTableKey)
                        ? "Row"
                        : ""
                    }`
                  : null}
              </S.DeleteTableButton>
            </S.DeleteTableButtonWrapper>
          </S.FixedDeleteButtonWrapper>
        )}

        <S.GroupShellWrapper isTableScroll={isTableScroll}>
          <S.TitleWrapper ref={titleRef}>
            {getGroupShellTitle(articleSection, groupShellKey) && (
              <TableCreatorGroupShellTitle
                groupShellKey={groupShellKey}
                groupShellCount={groupShellCount}
                groupShellIndex={groupShellIndex}
                isAlertGroupShell={isAlertGroupShell}
                onClickHandler={handleFocusGroupShell}
              />
            )}
          </S.TitleWrapper>
          <S.SelectWrapper>
            <S.GroupShellSelectButtonDividerWrapper
              ref={selectButtonRef}
              onClick={handleFocusGroupShell}
              isPossibleSelected={isPossibleSelected}
            >
              <S.GroupShellSelectButtonDivider className="select-button divider" />
              <S.GroupShellSelectButton className="select-button button">
                SELECT
              </S.GroupShellSelectButton>
            </S.GroupShellSelectButtonDividerWrapper>
            <S.GroupShell>
              {shellList.map(({ shellKey }, shellListIndex) => (
                <React.Fragment key={`shellList-${String(shellListIndex)}`}>
                  {newArrayToCountValue(
                    shellCount(shellKey, tableIndex, groupShellIndex),
                  ).map((_, shellIndex) => (
                    <RegulatoryTableCreatorShell
                      key={`shell-${String(groupTableIndex)}-${String(
                        tableIndex,
                      )}-${String(groupShellIndex)}-${String(shellIndex)}`}
                      groupTableKey={groupTableKey}
                      groupTableIndex={groupTableIndex}
                      groupShellKey={groupShellKey}
                      groupShellIndex={groupShellIndex}
                      shellKey={shellKey}
                      shellIndex={shellIndex}
                      tableKey={tableKey}
                      tableIndex={tableIndex}
                      isShellOverOne={
                        shellCount(shellKey, tableIndex, groupShellIndex) > 1
                      }
                      isSeparatedShellTitle={isSeparatedShellTitle(
                        articleSection,
                        groupShellKey,
                      )}
                      isFocusedGroupShell={isFocused}
                      isClearGroupShell={isClearGroupShell}
                      isDeleteGroupShell={isDeleteGroupShell}
                      isAlertGroupShell={isAlertGroupShell}
                    />
                  ))}
                </React.Fragment>
              ))}
            </S.GroupShell>
          </S.SelectWrapper>
        </S.GroupShellWrapper>
      </S.GroupShellOutsideWrapper>
      {isAddDeleteGroupShell(articleSection, groupShellKey) &&
        isLastGroupShell && (
          <GroupShellDividerPlusBar
            onClick={(event) =>
              clickAddGroupShellButton(event, {
                tableIndexParameter: tableIndex,
                groupShellIndexParameter: groupShellIndex + 1,
                groupShellKeyParameter: groupShellKey,
              })
            }
            isLastPosition
            isVisibility={isFocusedAddButton}
          />
        )}
    </>
  );
};

export default RegulatoryTableCreatorGroupShell;
